import {
  getDemoUserAvatarPath,
  isDemoUserId,
} from "@bezoned/shared/utils/demo-users.js";
import { Effect, Either, Fiber } from "effect";
import { useEffect } from "react";

import { getUserPhoto } from "../services/api.service.js";
import { useUserImageStore } from "../store/user-image.store.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { retryEffect } from "../utils/retry-with-effect.js";

interface UseUserPhotoProps {
  msUserId: string;
}

interface InFlightPhotoRequest {
  consumers: number;
  completed: boolean;
  fiber: Fiber.RuntimeFiber<void, never> | null;
}

const inFlightPhotoRequests = new Map<string, InFlightPhotoRequest>();

function getCachedPhotoStatus(msUserId: string) {
  const store = useUserImageStore.getState();

  return {
    photo: store.getPhoto(msUserId),
    status: store.getPhotoStatus(msUserId),
  };
}

function startPhotoRequest(msUserId: string): InFlightPhotoRequest {
  const existingRequest = inFlightPhotoRequests.get(msUserId);
  if (existingRequest) {
    existingRequest.consumers += 1;
    return existingRequest;
  }

  useUserImageStore.getState().setLoading(msUserId);

  const request: InFlightPhotoRequest = {
    completed: false,
    consumers: 1,
    fiber: null,
  };
  const requestEffect = retryEffect(() => getUserPhoto({ msUserId }));

  request.fiber = Effect.runFork(
    Effect.either(requestEffect).pipe(
      Effect.flatMap((result) =>
        Effect.sync(() => {
          if (
            request.completed ||
            inFlightPhotoRequests.get(msUserId) !== request
          ) {
            return;
          }

          request.completed = true;
          inFlightPhotoRequests.delete(msUserId);

          const store = useUserImageStore.getState();
          if (Either.isRight(result)) {
            if (result.right) {
              store.setPhoto(msUserId, result.right.pictureUrl);
            } else {
              store.setMissing(msUserId);
            }

            return;
          }

          recordErrorInSentry(result.left);
          store.setError(msUserId);
        })
      )
    )
  );

  inFlightPhotoRequests.set(msUserId, request);

  return request;
}

function releasePhotoRequest(msUserId: string, request: InFlightPhotoRequest) {
  if (request.completed || inFlightPhotoRequests.get(msUserId) !== request) {
    return;
  }

  request.consumers -= 1;
  if (request.consumers > 0) {
    return;
  }

  request.completed = true;
  inFlightPhotoRequests.delete(msUserId);
  useUserImageStore.getState().clearLoading(msUserId);

  if (request.fiber) {
    void Effect.runPromise(Fiber.interrupt(request.fiber));
  }
}

export function useUserPhoto({ msUserId }: UseUserPhotoProps) {
  const cachedPhotoUrl = useUserImageStore(
    (state) => state.photos.get(msUserId) ?? null
  );
  const cachedPhotoStatus = useUserImageStore(
    (state) => state.photoStatuses.get(msUserId) ?? null
  );
  const setPhoto = useUserImageStore((state) => state.setPhoto);

  useEffect(() => {
    if (!msUserId) {
      return;
    }

    const { photo: currentPhoto, status: currentStatus } =
      getCachedPhotoStatus(msUserId);

    if (isDemoUserId(msUserId)) {
      const demoPhoto = getDemoUserAvatarPath(msUserId);
      if (currentPhoto !== demoPhoto) {
        setPhoto(msUserId, demoPhoto);
      }
      return;
    }

    if (currentPhoto) {
      return;
    }

    if (currentStatus === "missing" || currentStatus === "error") {
      return;
    }

    const request = startPhotoRequest(msUserId);

    return () => releasePhotoRequest(msUserId, request);
  }, [msUserId, setPhoto]);

  return {
    avatar: {
      src: cachedPhotoUrl ?? undefined,
    },

    loading: cachedPhotoStatus === "loading",
  };
}
